/**
 * Individual Rower Power (IRP) chart - D3.js implementation.
 * Drop-in replacement for PerformanceMetrics.tsx (Chart.js) with full UX/visual parity.
 * No zoom, pan, reset, or arrow keys - Chart.js IRP has none of these; only view toggles.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { getChartChrome } from '../../../../theme/chartChrome';
import ChartContainer from '../common/ChartContainer';
import ViewToggle from '../common/ViewToggle';
import PerformanceMetricsTable from './PerformanceMetricsTable';
import {
  ChartProps,
  useChartPropsPattern,
  ChartEmptyState,
} from '../../../../utils/chartPropsPattern';
import { fetchPerformanceMetricsData } from '../../../../utils/chartDataFetchers';
import { applyPowerCorrections } from '../../../../utils/parameterCorrections';
import { useErrorTracking } from '../../../../hooks/useErrorTracking';
import { useTheme } from '../../../../contexts/ThemeContext';
import {
  hullSetFilterSubtitle,
  useHullStabilityFilterOptional,
} from '../../../../contexts/HullStabilityFilterContext';
import { aggregatePerformanceMetricsFromStrokeRows } from '../../../../utils/hullStability/filterStrokePowers';
import { CHART_TOOLTIP_CLASS, chartTooltipHtml as tt } from '../common/chartTooltipClasses';

/** Rower data shape after corrections (matches Chart.js usage) */
interface RowerDataItem {
  id: string | number;
  position?: string | number;
  power: number;
  min?: number;
  max?: number;
  p25?: number;
  p75?: number;
  peak?: number;
  consistency?: number;
  powerDistribution?: Array<{ start: number; end: number; count: number; frequency: number }>;
}

const DEFAULT_IRP_VIEW_STATES = {
  ranges: false,
  percentiles: true,
  peaks: true,
  density: true,
} as const;

const PerformanceMetricsD3: React.FC<
  ChartProps & { rowerContext?: import('../../../../types/rowerContext').RowerContext | null }
> = ({ sessionId, parameterInfo, className, rowerContext }) => {
  const [viewStates, setViewStates] = useState({ ...DEFAULT_IRP_VIEW_STATES });
  const [focusedRowerId, setFocusedRowerId] = useState<string | number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const chartAreaSizeRef = useRef({ width: 0, height: 0 });
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const prevViewStatesRef = useRef(viewStates);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [chartAreaSize, setChartAreaSize] = useState<{ width: number; height: number } | null>(
    null
  );

  const { reportError, trackInteraction } = useErrorTracking('PerformanceMetrics');
  const { resolved: resolvedTheme } = useTheme();
  const hullFilter = useHullStabilityFilterOptional();

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const {
    data: performanceData,
    loading,
    error,
    corrections,
  } = useChartPropsPattern(sessionId, parameterInfo, fetchPerformanceMetricsData, {
    queryKey: ['performance-metrics-chart'],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const handleViewChange = (view: keyof typeof viewStates) => {
    trackInteraction('performance_metrics_view_toggle', {
      sessionId,
      view,
      newState: !viewStates[view],
      currentViewStates: viewStates,
    });
    setViewStates((prev) => ({ ...prev, [view]: !prev[view] }));
  };

  const effectivePerformanceData = useMemo(() => {
    if (!performanceData || !hullFilter?.isFilterActive) return performanceData;
    const filtered = aggregatePerformanceMetricsFromStrokeRows(
      hullFilter.strokeMetricRows,
      null,
      hullFilter.allowedStrokeIndices
    );
    if (!filtered) return performanceData;
    return {
      ...performanceData,
      ...filtered,
      seatIdMapping: performanceData.seatIdMapping,
    };
  }, [performanceData, hullFilter]);

  const rowerData = useMemo(() => {
    return (
      effectivePerformanceData?.enhancedRowerData?.map((rower) => ({
        ...rower,
        name: `Rower ${rower.id}`,
        position: rower.id,
        power: applyPowerCorrections([rower.power], corrections.correction)[0],
        min: rower.min ? applyPowerCorrections([rower.min], corrections.correction)[0] : rower.min,
        max: rower.max ? applyPowerCorrections([rower.max], corrections.correction)[0] : rower.max,
        p25: rower.p25 ? applyPowerCorrections([rower.p25], corrections.correction)[0] : rower.p25,
        p75: rower.p75 ? applyPowerCorrections([rower.p75], corrections.correction)[0] : rower.p75,
        peak: rower.peak
          ? applyPowerCorrections([rower.peak], corrections.correction)[0]
          : rower.peak,
      })) || []
    );
  }, [effectivePerformanceData?.enhancedRowerData, corrections.correction]);

  const hasData = rowerData.length > 0;

  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [hasData]);

  // Observe chart area so D3 redraws when the chart container is resized or gets layout dimensions.
  // Delayed re-measure so we get correct size on first load / small spaces when layout settles.
  useEffect(() => {
    if (!rowerData.length) return;
    const el = chartAreaRef.current;
    if (!el) return;
    const syncSize = (w: number, h: number) => {
      if (w <= 0 || h <= 0) return;
      if (w !== chartAreaSizeRef.current.width || h !== chartAreaSizeRef.current.height) {
        chartAreaSizeRef.current = { width: w, height: h };
        setChartAreaSize({ width: w, height: h });
      }
    };
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      syncSize(Math.round(width), Math.round(height));
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    syncSize(Math.round(rect.width), Math.round(rect.height));
    // Re-measure after layout settles so first load and small/prototype views get correct size
    const t1 = setTimeout(() => {
      const r = el.getBoundingClientRect();
      syncSize(Math.round(r.width), Math.round(r.height));
    }, 50);
    const t2 = setTimeout(() => {
      const r = el.getBoundingClientRect();
      syncSize(Math.round(r.width), Math.round(r.height));
    }, 200);
    return () => {
      ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [rowerData.length]);

  const seatIdMapping = performanceData?.seatIdMapping || null;
  const nameMapping: Record<string | number, string> | null =
    (seatIdMapping as any)?.__nameMapping || null;

  useEffect(() => {
    if (error) {
      reportError(new Error(error), {
        context: 'performance_metrics_error',
        sessionId,
        chartType: 'performance_metrics',
      });
    }
  }, [error, sessionId, reportError]);

  const effectiveWidth = Math.min(containerWidth, windowWidth);
  const useHorizontalLayout = effectiveWidth >= 800;

  const tableWidth = useMemo(() => {
    if (!useHorizontalLayout) return 'w-full';
    return 'w-full';
  }, [useHorizontalLayout]);

  const tableStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        headerPadding: 'py-0.5 px-0.5',
        cellPadding: 'px-0.5 py-0.5',
        headerFont: 'text-[0.625rem]',
        cellFont: 'text-[0.625rem]',
        rowGap: 'gap-0.5',
      };
    }
    if (containerWidth < 600) {
      return {
        headerPadding: 'py-0.5 px-1',
        cellPadding: 'px-1 py-0.5',
        headerFont: 'text-[0.6875rem]',
        cellFont: 'text-[0.6875rem]',
        rowGap: 'gap-0.5',
      };
    }
    if (containerWidth < 800) {
      return {
        headerPadding: 'py-0.5 px-1.5',
        cellPadding: 'px-1.5 py-0.5',
        headerFont: 'text-[0.6875rem]',
        cellFont: 'text-[0.6875rem]',
        rowGap: 'gap-0.5',
      };
    }
    return {
      headerPadding: 'py-0.5 lg:py-1 px-1.5 lg:px-2',
      cellPadding: 'px-1.5 lg:px-2 py-0.5 lg:py-1',
      headerFont: 'text-[0.6875rem] lg:text-xs',
      cellFont: 'text-[0.6875rem] lg:text-xs',
      rowGap: 'gap-0.5',
    };
  }, [containerWidth]);

  // getChartChrome reads computed CSS tokens on <html> — re-run when theme resolves.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resolvedTheme invalidates DOM token reads
  const chartChrome = useMemo(() => getChartChrome(), [resolvedTheme]);

  const tooltipArrowStyles = useMemo(() => {
    return `
                  .irp-tooltip { transition: opacity 0.28s ease-out, left 0.18s ease-out, top 0.18s ease-out; }
                  .irp-tooltip::after { content: ''; position: absolute; right: -6px; top: 50%; transform: translateY(-50%); border: 6px solid transparent; border-left-color: rgb(var(--chart-tooltip-bg)); width: 0; height: 0; }
                  .irp-tooltip.irp-tooltip-right::after { right: auto; left: -6px; border-left-color: transparent; border-right-color: rgb(var(--chart-tooltip-bg)); }
                  .irp-tooltip-header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
                  .irp-tooltip-dot { width: 6px; height: 6px; border-radius: 50%; background: rgb(var(--chart-overlay-peaks)); flex-shrink: 0; }
                  .irp-tooltip-line { display: flex; align-items: center; gap: 6px; color: rgb(var(--chart-tooltip-fg)); }
                  .irp-tooltip-swatch { width: 10px; height: 10px; background: ${chartChrome.barRange}; flex-shrink: 0; }
                `;
  }, [chartChrome]);

  // D3 render: match Chart.js layout and visuals exactly; redraw on resize via containerWidth + chartAreaSize
  useEffect(() => {
    if (!rowerData.length || !svgRef.current) return;

    // Determine which toggles just turned on (only those should animate)
    const prev = prevViewStatesRef.current;
    const animate = {
      ranges: viewStates.ranges && !prev.ranges,
      percentiles: viewStates.percentiles && !prev.percentiles,
      peaks: viewStates.peaks && !prev.peaks,
      density: viewStates.density && !prev.density,
    };
    prevViewStatesRef.current = viewStates;

    const chartContainer =
      chartAreaRef.current ??
      (containerRef.current?.querySelector('[data-irp-d3-chart-area]') as HTMLDivElement | null);
    if (!chartContainer) return;

    let width = chartAreaSize?.width ?? chartContainer.clientWidth;
    let height = chartAreaSize?.height ?? chartContainer.clientHeight;
    // Fallback so chart draws on first paint before ResizeObserver reports; fill available space
    if (width <= 0) width = Math.max(400, containerWidth - 120);
    if (height <= 0) height = 320;
    if (width <= 0 || height <= 0) return;

    const margin = { top: 20, right: 20, bottom: 18, left: 50 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    const allValues = rowerData
      .map((r) => [r.power || 0, r.peak || 0, r.max || 0, r.p75 || 0])
      .flat();
    const actualMax = Math.max(...allValues, 100);
    const xMax = Math.ceil(actualMax * 1.2);

    const xScale = d3.scaleLinear().domain([0, xMax]).range([0, plotWidth]);
    const yScale = d3
      .scaleBand<number>()
      .domain(rowerData.map((_, i) => i))
      .range([0, plotHeight])
      .padding(0.2)
      .paddingOuter(0.1);

    const barHeight = yScale.bandwidth();

    const chrome = getChartChrome();

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height).attr('viewBox', `0 0 ${width} ${height}`);

    const defs = svg.append('defs');
    const filter = defs.append('filter').attr('id', 'irp-peak-shadow');
    filter
      .append('feDropShadow')
      .attr('flood-color', 'rgba(220,38,38,0.4)')
      .attr('stdDeviation', 2);
    // Golden/amber glow for focused rower border (match Chart.js shadowBlur: 8, amber)
    const focusedGlow = defs.append('filter').attr('id', 'irp-focused-glow');
    focusedGlow
      .append('feDropShadow')
      .attr('flood-color', 'rgba(245, 158, 11, 0.6)')
      .attr('stdDeviation', 4)
      .attr('dx', 0)
      .attr('dy', 0);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // X axis - match Chart.js: grid #f1f5f9, ticks font 11 color #6b7280
    const tickCount = Math.max(4, Math.min(10, Math.floor(plotWidth / 90)));
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(tickCount)
      .tickSizeInner(-plotHeight)
      .tickSizeOuter(0);
    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xAxis)
      .call((sel) => {
        sel.select('.domain').remove();
        sel.selectAll('line').attr('stroke', chrome.grid);
        sel.selectAll('.tick text').attr('font-size', '11px').attr('fill', chrome.axisTick);
      });

    // Y axis - categories R1, R2, ... with rower name when mapped; font 11 color #6b7280
    const yAxis = d3
      .axisLeft(yScale)
      .tickFormat((i) => {
        const rower = rowerData[Number(i)];
        const pos = rower?.position ?? i;
        return `R${pos}`;
      })
      .tickSize(0)
      .tickPadding(6);
    g.append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .call((sel) => {
        sel.select('.domain').remove();
        sel.selectAll('.tick text').attr('font-size', '11px').attr('fill', chrome.axisTick);
        // Dim y-axis labels when a row is focused
        if (focusedRowerId != null) {
          sel.selectAll('.tick').each(function (d) {
            const idx = Number(d);
            const rower = rowerData[idx];
            if (rower) {
              const rowerId = rower.position ?? rower.id;
              const isDimmed = rowerId !== focusedRowerId;
              d3.select(this).attr('opacity', isDimmed ? 0.3 : 1);
            }
          });
        }
      });

    rowerData.forEach((rower, i) => {
      const y = (yScale(i) ?? 0) + barHeight / 2;
      const rowerId = rower.position ?? rower.id;
      const isFocusedByContext = rowerContext ? rowerId === rowerContext.position : false;
      const isFocusedByClick = focusedRowerId != null ? rowerId === focusedRowerId : false;
      const isFocused = isFocusedByContext || isFocusedByClick;
      const isDimmed = focusedRowerId != null && rowerId !== focusedRowerId;

      // Wrap each rower in a group for opacity dimming
      const rowGroup = g
        .append('g')
        .attr('opacity', isDimmed ? 0.3 : 1)
        .style('transition', 'opacity 0.2s ease');

      // 1. Full-width track background
      rowGroup
        .append('rect')
        .attr('x', 0)
        .attr('y', yScale(i) ?? 0)
        .attr('width', plotWidth)
        .attr('height', barHeight)
        .attr('fill', chrome.barTrack)
        .attr('rx', 8);

      // 2. Range overlay (animated: expand from center)
      if (viewStates.ranges && rower.min != null && rower.max != null) {
        const xMin = xScale(rower.min);
        const xMaxVal = xScale(rower.max);
        const rangeWidth = Math.max(0, xMaxVal - xMin);
        const rangeMid = xMin + rangeWidth / 2;
        const rect = rowGroup
          .append('rect')
          .attr('y', (yScale(i) ?? 0) + barHeight * 0.1)
          .attr('height', barHeight * 0.8)
          .attr('fill', chrome.barRange);
        if (animate.ranges) {
          rect
            .attr('x', rangeMid)
            .attr('width', 0)
            .transition()
            .duration(300)
            .delay(i * 20)
            .ease(d3.easeCubicOut)
            .attr('x', xMin)
            .attr('width', rangeWidth);
        } else {
          rect.attr('x', xMin).attr('width', rangeWidth);
        }
      }

      // 3. Density overlay - amber bins (animated: sweep left to right, all rows together)
      if (viewStates.density && rower.powerDistribution?.length) {
        const maxCount = Math.max(...rower.powerDistribution.map((b) => b.count));
        const bandY = yScale(i) ?? 0;
        rower.powerDistribution.forEach((bin, binIdx) => {
          const xStart = xScale(bin.start);
          const xEnd = xScale(bin.end);
          const binWidth = Math.max(0, xEnd - xStart);
          const alpha = 0.3 + (bin.count / maxCount) * 0.7;
          const densityColor = d3.color(chrome.overlayDensity);
          const rect = rowGroup
            .append('rect')
            .attr('x', xStart)
            .attr('y', bandY)
            .attr('height', barHeight)
            .attr(
              'fill',
              densityColor
                ? densityColor.copy({ opacity: alpha }).formatRgb()!
                : `rgba(245, 158, 11, ${alpha})`
            );
          if (animate.density) {
            rect
              .attr('width', 0)
              .transition()
              .duration(250)
              .delay(binIdx * 15)
              .ease(d3.easeCubicOut)
              .attr('width', binWidth);
          } else {
            rect.attr('width', binWidth);
          }
        });
      }

      // 4. Percentile overlay - #64748b, height 50% (animated: expand from center)
      if (viewStates.percentiles && rower.p25 != null && rower.p75 != null) {
        const x25 = xScale(rower.p25);
        const x75 = xScale(rower.p75);
        const percWidth = Math.max(0, x75 - x25);
        const percMid = x25 + percWidth / 2;
        const rect = rowGroup
          .append('rect')
          .attr('y', (yScale(i) ?? 0) + barHeight * 0.25)
          .attr('height', barHeight * 0.5)
          .attr('fill', chrome.barPercentile);
        if (animate.percentiles) {
          rect
            .attr('x', percMid)
            .attr('width', 0)
            .transition()
            .duration(300)
            .delay(i * 20)
            .ease(d3.easeCubicOut)
            .attr('x', x25)
            .attr('width', percWidth);
        } else {
          rect.attr('x', x25).attr('width', percWidth);
        }
      }

      // 5. Average line - #374151, stroke-width 3
      const xAvg = xScale(rower.power);
      rowGroup
        .append('line')
        .attr('x1', xAvg)
        .attr('x2', xAvg)
        .attr('y1', (yScale(i) ?? 0) + 2)
        .attr('y2', (yScale(i) ?? 0) + barHeight - 2)
        .attr('stroke', chrome.axisLabel)
        .attr('stroke-width', 3);

      // 6. Peak dot - radius 7, #dc2626, white stroke 3 (animated: slide from average line)
      if (viewStates.peaks && rower.peak != null) {
        const xPeak = xScale(rower.peak);
        const circle = rowGroup
          .append('circle')
          .attr('cy', y)
          .attr('fill', chrome.overlayPeaks)
          .attr('stroke', chrome.peakStroke)
          .attr('stroke-width', 3)
          .attr('r', 7);
        if (animate.peaks) {
          circle
            .attr('cx', xAvg)
            .attr('opacity', 0)
            .transition()
            .duration(400)
            .delay(i * 30)
            .ease(d3.easeCubicOut)
            .attr('cx', xPeak)
            .attr('opacity', 1);
        } else {
          circle.attr('cx', xPeak);
        }
      }

      // 7. Focused rower border - #f59e0b, width 4, golden glow (match Chart.js shadowBlur: 8)
      if (isFocused) {
        rowGroup
          .append('rect')
          .attr('x', 0)
          .attr('y', yScale(i) ?? 0)
          .attr('width', 4)
          .attr('height', barHeight)
          .attr('fill', 'none')
          .attr('stroke', '#f59e0b')
          .attr('stroke-width', 4)
          .attr('filter', 'url(#irp-focused-glow)');
        rowGroup
          .append('rect')
          .attr('x', plotWidth - 4)
          .attr('y', yScale(i) ?? 0)
          .attr('width', 4)
          .attr('height', barHeight)
          .attr('fill', 'none')
          .attr('stroke', '#f59e0b')
          .attr('stroke-width', 4)
          .attr('filter', 'url(#irp-focused-glow)');
      }
    });

    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;

    const chartArea = (g.node() as SVGGElement)?.ownerSVGElement?.parentElement as
      | HTMLElement
      | undefined;
    const FADE_MS = 280;
    let hideTimeout: ReturnType<typeof setTimeout> | null = null;
    let switchTimeout: ReturnType<typeof setTimeout> | null = null;
    let prevRowIndex = -1;
    let lastClientX = 0;
    let lastClientY = 0;

    const setTooltipContent = (rower: RowerDataItem) => {
      const rowerName = `R${rower.position ?? rower.id}`;
      const metrics = [
        `Avg: ${rower.power}W`,
        rower.consistency !== undefined ? `Cons: ${rower.consistency}%` : '',
        rower.peak !== undefined ? `Peak: ${rower.peak}W` : '',
        rower.min !== undefined && rower.max !== undefined
          ? `Range: ${rower.min} - ${rower.max}W`
          : '',
        rower.p25 !== undefined && rower.p75 !== undefined
          ? `P25-P75: ${rower.p25}-${rower.p75}W`
          : '',
      ].filter(Boolean);
      tooltipEl.innerHTML = [
        `<div class="irp-tooltip-header"><span class="${tt.value}">${rowerName}</span><span class="irp-tooltip-dot"></span></div>`,
        ...metrics.map((line, i) =>
          i === 0
            ? `<div class="irp-tooltip-line"><span class="irp-tooltip-swatch"></span>${line}</div>`
            : `<div class="irp-tooltip-line">${line}</div>`
        ),
      ].join('');
    };

    const setTooltipPosition = (clientX: number, clientY: number) => {
      const gap = 14;
      const edge = 8;
      if (chartArea) {
        const rect = chartArea.getBoundingClientRect();
        const tooltipWidth = tooltipEl.offsetWidth || 160;
        const tooltipHeight = tooltipEl.offsetHeight || 40;
        let left = clientX - rect.left - tooltipWidth - gap;
        let top = clientY - rect.top - 8;
        let placeRight = false;

        if (left < edge) {
          left = clientX - rect.left + gap;
          placeRight = true;
        }

        if (left + tooltipWidth > rect.width - edge) {
          left = rect.width - tooltipWidth - edge;
        }
        if (top < edge) top = edge;
        if (top + tooltipHeight > rect.height - edge) {
          top = rect.height - tooltipHeight - edge;
        }

        tooltipEl.classList.toggle('irp-tooltip-right', placeRight);
        tooltipEl.style.left = `${left}px`;
        tooltipEl.style.top = `${top}px`;
      } else {
        tooltipEl.classList.remove('irp-tooltip-right');
        tooltipEl.style.left = `${clientX - 174}px`;
        tooltipEl.style.top = `${clientY - 8}px`;
      }
    };

    const showTooltipNow = (rower: RowerDataItem, clientX: number, clientY: number) => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      setTooltipContent(rower);
      setTooltipPosition(clientX, clientY);
      tooltipEl.style.visibility = 'visible';
      tooltipEl.style.opacity = '1';
    };

    const hideTooltip = () => {
      if (switchTimeout) {
        clearTimeout(switchTimeout);
        switchTimeout = null;
      }
      if (hideTimeout) clearTimeout(hideTimeout);
      tooltipEl.style.opacity = '0';
      hideTimeout = setTimeout(() => {
        tooltipEl.style.visibility = 'hidden';
        hideTimeout = null;
        prevRowIndex = -1;
      }, FADE_MS + 30);
    };

    const overlay = g
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('fill', 'transparent')
      .attr('pointer-events', 'all');

    overlay
      .on('mousemove', function (event: MouseEvent) {
        lastClientX = event.clientX;
        lastClientY = event.clientY;
        const [, py] = d3.pointer(event, g.node());
        const step = yScale.step();
        const rangeStart = yScale.range()[0] ?? 0;
        const rawIndex = (py - rangeStart) / step;
        const rowIndex = Math.max(0, Math.min(rowerData.length - 1, Math.floor(rawIndex)));
        const rower = rowerData[rowIndex];

        if (!rower) return;

        if (prevRowIndex === -1) {
          showTooltipNow(rower, lastClientX, lastClientY);
          prevRowIndex = rowIndex;
          return;
        }

        if (rowIndex === prevRowIndex) {
          setTooltipPosition(lastClientX, lastClientY);
          return;
        }

        // Row changed: fade out, then show new rower and fade in
        if (switchTimeout) clearTimeout(switchTimeout);
        tooltipEl.style.opacity = '0';
        prevRowIndex = rowIndex;
        const rowerToShow = rower;
        switchTimeout = setTimeout(() => {
          setTooltipContent(rowerToShow);
          setTooltipPosition(lastClientX, lastClientY);
          tooltipEl.style.visibility = 'visible';
          tooltipEl.style.opacity = '0';
          requestAnimationFrame(() => {
            tooltipEl.style.opacity = '1';
          });
          switchTimeout = null;
        }, FADE_MS);
      })
      .on('mouseleave', hideTooltip);
  }, [
    rowerData,
    viewStates,
    rowerContext,
    focusedRowerId,
    containerWidth,
    chartAreaSize,
    resolvedTheme,
  ]);

  return (
    <ChartContainer
      title="Individual Rower Power"
      subtitle={hullSetFilterSubtitle(
        rowerContext
          ? `Highlighting ${rowerContext.displayName} - comparative view with crew`
          : 'Comparative power output visualization for all crew members',
        hullFilter
      )}
      loading={loading}
      maximizable
    >
      {rowerData.length > 0 ? (
        <div
          ref={containerRef}
          className={`bg-surface rounded-lg p-2 w-full h-full flex flex-col ${className || ''}`}
        >
          <div
            className={`flex ${useHorizontalLayout ? 'flex-row' : 'flex-col'} gap-3 lg:gap-6 flex-1 min-h-0 overflow-hidden`}
          >
            <div
              className={`${useHorizontalLayout ? 'w-[28%] min-w-[160px] max-w-[260px] flex-shrink-0 overflow-auto' : 'flex-shrink-0 max-h-[35%] overflow-auto'} border-b border-border`}
            >
              <PerformanceMetricsTable
                rowerData={rowerData}
                rowerContext={rowerContext}
                nameMapping={nameMapping}
                tableWidth={tableWidth}
                tableStyles={tableStyles}
                useHorizontalLayout={useHorizontalLayout}
                focusedRowerId={focusedRowerId}
                onRowClick={(id) => setFocusedRowerId((prev) => (prev === id ? null : id))}
              />
            </div>
            <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
              <div className="flex justify-end items-center mb-2 flex-shrink-0">
                <ViewToggle
                  views={viewStates}
                  onViewChange={handleViewChange}
                  className="flex-wrap"
                  containerWidth={containerWidth}
                />
              </div>
              <div
                ref={chartAreaRef}
                className="flex-1 min-h-0 min-w-0 overflow-hidden relative w-full h-0"
                data-irp-d3-chart-area
              >
                <style>{tooltipArrowStyles}</style>
                <svg
                  ref={svgRef}
                  className="w-full h-full"
                  style={{ display: 'block' }}
                  aria-label="Individual rower power comparison"
                />
                <div
                  ref={tooltipRef}
                  className={`irp-tooltip ${CHART_TOOLTIP_CLASS} whitespace-nowrap`}
                  style={{ visibility: 'hidden', opacity: 0 }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 flex w-full justify-between text-xs text-content-secondary border-t border-border p-2 mt-3">
            <span>
              Avg Consistency:{' '}
              <span className="font-medium">
                {Math.round(
                  rowerData.reduce((sum, r) => sum + (r.consistency || 0), 0) / rowerData.length
                )}
                %
              </span>
            </span>
            <span>
              Power Range:{' '}
              <span className="font-medium">
                {Math.min(...rowerData.map((r) => r.power))}W -{' '}
                {Math.max(...rowerData.map((r) => r.power))}W
              </span>
            </span>
          </div>
        </div>
      ) : (
        <ChartEmptyState message="Individual rower power comparisons will be displayed here once uploaded" />
      )}
    </ChartContainer>
  );
};

export default PerformanceMetricsD3;
